import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search, ArrowRight } from "lucide-react";
import type { SearchHit, SearchSuggestion } from "@/lib/smartSearch";

export type FacetId = "all" | "inventory" | "guide" | "blog" | "page";

const FACETS: { id: FacetId; label: string }[] = [
  { id: "all", label: "All" },
  { id: "inventory", label: "Unit Inventory" },
  { id: "guide", label: "Guides" },
  { id: "blog", label: "Blog" },
  { id: "page", label: "General Pages" },
];

/** Map an index document type onto a visitor-facing facet. */
export function facetOf(hit: SearchHit): Exclude<FacetId, "all"> {
  if (hit.doc.source === "inventory") return "inventory";
  if (hit.doc.type === "guide") return "guide";
  if (hit.doc.type === "blog") return "blog";
  return "page";
}

function formatSuggestionCounts(counts: SearchSuggestion["counts"]): string {
  const parts = [
    counts.inventory > 0 ? `${counts.inventory} Unit${counts.inventory === 1 ? "" : "s"}` : "",
    counts.guide > 0 ? `${counts.guide} Guide${counts.guide === 1 ? "" : "s"}` : "",
    counts.blog > 0 ? `${counts.blog} Blog post${counts.blog === 1 ? "" : "s"}` : "",
    counts.page > 0 ? `${counts.page} General page${counts.page === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  if (parts.length === 0) return "No results";
  return parts.join(" · ");
}

interface Props {
  query: string;
  hits: SearchHit[];
  suggestions: string[] | SearchSuggestion[];
  loading?: boolean;
  /** Visible rows per facet before "show all". */
  visibleCount?: number;
  /** Receives the full suggested query, ready to run. */
  onSuggestionClick?: (query: string) => void;
  showAllHref?: string;
  onShowAll?: () => void;
  /** When true, "Show all" navigates to /search with the current query + facet. */
  linkToResultsPage?: boolean;
  /** Force a single-column stack (used by the narrow device previews). */
  stacked?: boolean;
  /** Hide the keyword-suggestions sidebar. */
  hideSuggestions?: boolean;
  /** Move the "Show all results" footer above the results instead of below. */
  showAllAtTop?: boolean;
  /** Constrain the results list height and make it scrollable. */
  scrollable?: boolean;
}

/**
 * Storefront-style search dropdown: results on the left with facet filters,
 * keyword suggestions on the right, "show all results" in the footer.
 */
const SmartSearchPanel = ({
  query,
  hits,
  suggestions,
  loading = false,
  visibleCount = 6,
  onSuggestionClick,
  showAllHref,
  onShowAll,
  stacked = false,
  linkToResultsPage = false,
  hideSuggestions = false,
  showAllAtTop = false,
  scrollable = false,
}: Props) => {
  const [facet, setFacet] = useState<FacetId>("all");

  const normalizedSuggestions = useMemo<SearchSuggestion[]>(() => {
    return suggestions.map((s) =>
      typeof s === "string" ? { term: s, counts: { inventory: 0, guide: 0, blog: 0, page: 0, total: 0 } } : s,
    );
  }, [suggestions]);

  const counts = useMemo(() => {
    const c: Record<FacetId, number> = { all: hits.length, inventory: 0, guide: 0, blog: 0, page: 0 };
    for (const h of hits) c[facetOf(h)] += 1;
    return c;
  }, [hits]);

  const filtered = useMemo(
    () => (facet === "all" ? hits : hits.filter((h) => facetOf(h) === facet)),
    [hits, facet],
  );
  const visible = filtered.slice(0, visibleCount);

  const showAllBar =
    filtered.length > 0 ? (
      linkToResultsPage ? (
        <Link
          to="/search"
          search={{ q: query, facet: facet === "all" ? undefined : facet }}
          className="flex w-full items-center justify-center gap-2 border-y bg-muted/50 px-4 py-3 text-sm font-semibold text-primary hover:bg-muted"
        >
          <Search className="h-3.5 w-3.5" />
          Show all {filtered.length} results
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      ) : showAllHref ? (
        <Link
          to={showAllHref}
          className="flex w-full items-center justify-center gap-2 border-y bg-muted/50 px-4 py-3 text-sm font-semibold text-primary hover:bg-muted"
        >
          <Search className="h-3.5 w-3.5" />
          Show all {filtered.length} results
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      ) : (
        <button
          type="button"
          onClick={onShowAll}
          className="flex w-full items-center justify-center gap-2 border-y bg-muted/50 px-4 py-3 text-sm font-semibold text-primary hover:bg-muted"
        >
          <Search className="h-3.5 w-3.5" />
          Show all {filtered.length} results
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )
    ) : null;

  return (
    <div className="overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-xl">
      {showAllAtTop && showAllBar}

      <div className={stacked ? "grid" : "grid md:grid-cols-[minmax(0,1fr)_240px]"}>
        <div className="min-w-0 p-4">
          <h3 className="mb-3 border-b pb-1 text-sm font-semibold">
            {facet === "inventory" ? "Units" : facet === "all" ? "Results" : FACETS.find((f) => f.id === facet)?.label}
          </h3>
          {loading ? (
            <p className="py-6 text-sm text-muted-foreground">Searching…</p>
          ) : visible.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              No matches for <span className="font-semibold text-foreground">“{query}”</span>
            </p>
          ) : (
            <ul className={`divide-y ${scrollable ? "max-h-[320px] overflow-y-auto pr-1" : ""}`}>
              {visible.map((hit) => (
                <li key={hit.doc.id}>
                  <Link
                    to={hit.doc.path}
                    className="flex items-start gap-3 py-3 transition-colors hover:bg-muted/60"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{hit.doc.title}</span>
                      {hit.doc.price ? (
                        <span className="block text-sm font-bold text-primary">
                          ${hit.doc.price.toLocaleString()}
                        </span>
                      ) : null}
                      <span className="block truncate text-xs text-muted-foreground">
                        {[
                          hit.doc.manufacturer ?? hit.doc.brand,
                          hit.doc.rvType,
                          hit.doc.stockNumber ? `Stock #${hit.doc.stockNumber}` : null,
                          hit.doc.source === "pages" ? hit.doc.type : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside
          className={
            stacked
              ? "border-t bg-muted/30 p-4"
              : "border-t bg-muted/30 p-4 md:border-l md:border-t-0"
          }
        >
          <h3 className="mb-2 text-sm font-semibold">Filters</h3>
          <div className="mb-4 flex flex-wrap gap-1">
            {FACETS.map((f) => {
              const active = facet === f.id;
              const n = counts[f.id];
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFacet(f.id)}
                  disabled={f.id !== "all" && n === 0}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-40 ${
                    active
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/70"
                  }`}
                >
                  {f.label}
                  <span className="ml-1.5 opacity-70">{n}</span>
                </button>
              );
            })}
          </div>

          {!hideSuggestions && (
            <>
              <h3 className="mb-3 text-sm font-semibold">Suggestions</h3>
              {normalizedSuggestions.length === 0 ? (
                <p className="text-xs text-muted-foreground">No suggestions yet.</p>
              ) : (
                <ul className="space-y-2">
                  {normalizedSuggestions.map((s) => {
                    const baseWords =
                      s.base ?? query.trim().split(/\s+/).slice(0, -1).join(" ");
                    const fullQuery =
                      s.query ?? `${baseWords} ${s.term}`.trim();
                    return (
                    <li key={fullQuery}>
                      <button
                        type="button"
                        onClick={() => onSuggestionClick?.(fullQuery)}
                        className="group flex w-full items-center justify-between text-left text-sm text-muted-foreground hover:text-foreground"
                      >
                        <span className="min-w-0 truncate whitespace-pre">
                          {baseWords ? (
                            <span className="text-muted-foreground">{`${baseWords} `}</span>
                          ) : null}
                          <span className="font-semibold text-foreground">{s.term}</span>
                        </span>
                        <span className="ml-2 shrink-0 text-[11px] text-muted-foreground/70 group-hover:text-muted-foreground">
                          {formatSuggestionCounts(s.counts)}
                        </span>
                      </button>
                    </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </aside>
      </div>

      {!showAllAtTop && showAllBar}
    </div>
  );
}

export default SmartSearchPanel;
